import { Button, DatePicker, message, Table, Tag, Tooltip } from "antd";
import React, { useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import moment from "moment";
import dayjs from "dayjs";
import { token } from "../../constants/Constants";
import { get_test_report, get_test_report_all } from "../../endpoints/API";

const TestWiseReport = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const { RangePicker } = DatePicker;
  const [data, setData] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchReportData = async (dates, page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      const response = await get_test_report(dates?.[0], dates?.[1], page, size);
      const result = response.data;
      setData(result.results);
      setTotal(result.count);
    } catch (error) {
      message.error(error?.response?.data?.error || "Error fetching report data");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (value, dateString) => {
    setSelectedDates(dateString);
    setCurrentPage(1);
    fetchReportData(dateString, 1, pageSize);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    fetchReportData(selectedDates, pagination.current, pagination.pageSize);
  };

  const handleDownload = async () => {
    try {
      const response = await get_test_report_all(selectedDates?.[0], selectedDates?.[1]);
      const allData = response.data;
      const cols = [
        { title: "Order Number", dataIndex: "order_number" },
        { title: "Placed Date", dataIndex: "place_date" },
        { title: "Scheduled Date", dataIndex: "schedule_date" },
        { title: "Patient Name", dataIndex: "family_member_name" },
        { title: "Patient Phone No", dataIndex: "family_member_phone_number" },
        { title: "Patient Gender", dataIndex: "gender" },
        { title: "Test ID", dataIndex: "test_id" },
        { title: "Test Name", dataIndex: "test_name" },
        { title: "Test Amount", dataIndex: "test_amount" },
        { title: "B2B Total", dataIndex: "b2b_total" },
        { title: "Wellness Executive", dataIndex: "wellness_executive_name" },
        { title: "Lab Name", dataIndex: "lab" },
      ];
      const csvContent = [
        cols.map((c) => c.title).join(","),
        ...allData.map((row) =>
          cols.map((c) => {
            if (c.dataIndex === "schedule_date" && row[c.dataIndex])
              return moment(row[c.dataIndex]).format("DD/MM/YYYY hh:mm A");
            return row[c.dataIndex] || "";
          }).join(",")
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "TestWiseReport.csv";
      link.click();
    } catch (error) {
      message.error("Failed to download report");
    }
  };

  const columns = [
    { title: "Order Number", dataIndex: "order_number", key: "order_number", width: 120 },
    { title: "Placed Date", dataIndex: "place_date", key: "place_date", width: 150, render: (t) => t ? moment(t).format("DD/MM/YYYY hh:mm A") : "" },
    { title: "Scheduled Date", dataIndex: "schedule_date", key: "schedule_date", width: 150, render: (t) => t ? moment(t).format("DD/MM/YYYY hh:mm A") : "" },
    { title: "Report Generated", dataIndex: "report_generated", key: "report_generated", width: 150, render: (t) => t ? moment(t).format("DD/MM/YYYY hh:mm A") : "" },
    { title: "Register User", dataIndex: "registeruser", key: "registeruser", width: 120 },
    { title: "Patient Name", dataIndex: "family_member_name", key: "family_member_name", width: 130 },
    { title: "Patient Gender", dataIndex: "gender", key: "gender", width: 110 },
    { title: "DOB", dataIndex: "dob", key: "dob", width: 100 },
    { title: "Patient Ph No", dataIndex: "family_member_phone_number", key: "family_member_phone_number", width: 130, render: (t) => t ? `'${t}` : "-" },
    {
      title: "Location", dataIndex: "location", key: "location", width: 150,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ display: "inline-block", maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}>
            {text}
          </span>
        </Tooltip>
      ),
    },
    { title: "Test ID", dataIndex: "test_id", key: "test_id", width: 90, render: (t) => t || "-" },
    { title: "Test Name", dataIndex: "test_name", key: "test_name", width: 150 },
    { title: "Test Amount", dataIndex: "test_amount", key: "test_amount", width: 110 },
    { title: "B2B Total", dataIndex: "b2b_total", key: "b2b_total", width: 100 },
    { title: "Wellness Executive", dataIndex: "wellness_executive_name", key: "wellness_executive_name", width: 150, render: (t) => t || "-" },
    { title: "Lab Name", dataIndex: "lab", key: "lab", width: 120 },
  ];

  return (
    <div style={{ padding: "8px" }}>
      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
        <span className="font-semibold whitespace-nowrap">Total: <Tag>{total}</Tag></span>
        <div className="w-full md:flex-1 md:max-w-xs">
          <RangePicker
            format="YYYY-MM-DD"
            onChange={handleDateChange}
            disabledDate={(current) => current && current > dayjs().endOf("day")}
            style={{ width: "100%" }}
          />
        </div>
        <Button type="primary" onClick={handleDownload} disabled={!data.length} className="w-full md:w-auto">
          Download Report <DownloadOutlined />
        </Button>
      </div>
      <div style={{ overflowX: "auto", marginTop: 16 }}>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey={(_, i) => i}
          scroll={{ x: "max-content" }}
          size="small"
          pagination={{ current: currentPage, pageSize, total, showSizeChanger: true, responsive: true, size: "small" }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default TestWiseReport;